import { useEffect } from "react";

import { useMessengerStore } from "@/apps/messenger/stores/messenger-store";
import { subscribeToMomentoTopic } from "@/apps/messenger/momento-subscriber";
import {
  ensureLeaderElection,
  isTabLeader,
  subscribeToBroadcasts,
  subscribeToLeaderChange,
  broadcastFromLeader,
} from "@/realtime/tab-leader";
import { useUserStore } from "@slumbr/core";

interface TypingEvent {
  type?: string;
  conversationId?: string;
  senderId?: string;
  displayName?: string;
  isTyping?: boolean;
}

const TYPING_TTL_MS = 7_000;

interface ValidTypingEvent {
  type: "chat.typing";
  conversationId: string;
  senderId: string;
  displayName: string;
  isTyping: boolean;
}

/**
 * Typing payloads come straight from other members' browsers (direct
 * Momento publish, no server in between), so check the runtime shape
 * before it reaches the store — a non-string `displayName` would crash
 * every `.split()` renderer — and bind the event to the topic it arrived
 * on so a member can't paint indicators into some other conversation.
 */
export function parseTypingEvent(raw: unknown, conversationId: string): ValidTypingEvent | null {
  if (!raw || typeof raw !== "object") return null;
  const e = raw as TypingEvent;
  if (e.type !== "chat.typing") return null;
  if (e.conversationId !== conversationId) return null;
  if (typeof e.senderId !== "string" || !e.senderId || e.senderId.length > 128) return null;
  if (typeof e.isTyping !== "boolean") return null;
  const name = typeof e.displayName === "string" ? e.displayName.trim().slice(0, 64) : "";
  return {
    type: "chat.typing",
    conversationId,
    senderId: e.senderId,
    displayName: name || "Someone",
    isTyping: e.isTyping,
  };
}

// Live hook instances per conversation. The dock thread and the messenger
// page can both watch the same conversation; only the last one to unmount
// may clear that conversation's indicators from the shared store.
const activeSubscribers = new Map<string, number>();

/**
 * Subscribe to typing indicators for the active conversation only.
 *
 * Why active-conversation-only: typing is a high-frequency event that
 * only matters when the user is actually looking at the conversation.
 * Subscribing to typing for *every* conversation a user is a member of
 * would inflate Momento topic deliveries linearly with their conversation
 * count for zero UX benefit.
 *
 * Leader-gated: the visible tab's leader subscribes, then broadcasts the
 * event to sibling tabs over BroadcastChannel. Followers don't subscribe
 * to Momento directly — they receive a mirrored event from the leader.
 *
 * Stale handler protection: typing events auto-clear after
 * TYPING_TTL_MS on the receiver side. Kept above the publisher's 5 s
 * typing-start throttle so a continuous burst doesn't flicker off between
 * refreshes.
 */
export function useTypingSubscription(conversationId: string | null) {
  const currentUserId = useUserStore((s) => s.user?.id);

  useEffect(() => {
    if (!conversationId || !currentUserId) return;

    activeSubscribers.set(conversationId, (activeSubscribers.get(conversationId) ?? 0) + 1);
    let disposed = false;
    let momentoUnsub: (() => void) | null = null;
    let leaderUnsub: (() => void) | null = null;
    let broadcastUnsub: (() => void) | null = null;
    const typingTimers = new Map<
      string,
      { timer: number; conversationId: string; senderId: string }
    >();

    const applyTyping = (event: ValidTypingEvent) => {
      if (event.senderId === currentUserId) return;
      const store = useMessengerStore.getState();
      const key = `${event.conversationId}:${event.senderId}`;
      const existing = typingTimers.get(key);
      if (existing) window.clearTimeout(existing.timer);
      if (event.isTyping) {
        store.setTyping(event.conversationId, event.senderId, event.displayName);
        typingTimers.set(key, {
          timer: window.setTimeout(() => {
            useMessengerStore.getState().clearTyping(event.conversationId, event.senderId);
            typingTimers.delete(key);
          }, TYPING_TTL_MS),
          conversationId: event.conversationId,
          senderId: event.senderId,
        });
      } else {
        store.clearTyping(event.conversationId, event.senderId);
        typingTimers.delete(key);
      }
    };

    const startSubscription = async () => {
      if (momentoUnsub || disposed) return;
      const sub = await subscribeToMomentoTopic(`typing.${conversationId}`, (item) => {
        let event: ValidTypingEvent | null;
        try {
          event = parseTypingEvent(JSON.parse(item.valueString()), conversationId);
        } catch {
          return;
        }
        if (!event) return;
        applyTyping(event);
        if (isTabLeader()) {
          try {
            broadcastFromLeader({ type: "typing", payload: event });
          } catch (err) {
            console.warn("[typing] broadcast failed", err);
          }
        }
      }, { conversationIds: [conversationId] });
      if (disposed) {
        sub.unsubscribe();
        return;
      }
      momentoUnsub = () => sub.unsubscribe();
    };
    const stopSubscription = () => {
      momentoUnsub?.();
      momentoUnsub = null;
    };

    void ensureLeaderElection().then(() => {
      if (disposed) return;
      leaderUnsub = subscribeToLeaderChange((leader) => {
        if (leader) void startSubscription();
        else stopSubscription();
      });
      broadcastUnsub = subscribeToBroadcasts((envelope) => {
        if (isTabLeader()) return;
        if (envelope.type !== "typing") return;
        const event = parseTypingEvent(envelope.payload, conversationId);
        if (event) applyTyping(event);
      });
    });

    return () => {
      disposed = true;
      stopSubscription();
      leaderUnsub?.();
      broadcastUnsub?.();
      // Clear the store's typing indicators too — clearing only the local
      // timers left "X is typing…" stuck forever after switching
      // conversations (the auto-clear timeout died with the timer map).
      // Skipped while another instance still watches this conversation; its
      // own timers clear the same indicators on expiry.
      const remaining = (activeSubscribers.get(conversationId) ?? 1) - 1;
      if (remaining > 0) activeSubscribers.set(conversationId, remaining);
      else activeSubscribers.delete(conversationId);
      const store = useMessengerStore.getState();
      for (const entry of typingTimers.values()) {
        window.clearTimeout(entry.timer);
        if (remaining === 0) store.clearTyping(entry.conversationId, entry.senderId);
      }
      typingTimers.clear();
    };
  }, [conversationId, currentUserId]);
}
